import { type FunctionComponent, useCallback } from "react";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { useCurrentPageType, useTrackImpressionEventByIntersection } from "shared/hooks";
import type { PageType } from "shared/lib/page-type";
import { getAbsoluteUrl, notEmpty } from "shared/lib/utils";

import { EntityLink } from "scmp-app/components/entity-link";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type {
  ArticleRecirculationWidgetName,
  RecirculationWidgetImpressionEvent,
} from "scmp-app/components/tracking/google-analytics-4/types";
import type { WidgetVersion } from "scmp-app/pages/home/ab-widget-fetch";
import type { batchQueryAbWidgetQuery$data } from "scmp-app/queries/__generated__/batchQueryAbWidgetQuery.graphql";
import type { entityLink$key } from "scmp-app/queries/__generated__/entityLink.graphql";

import {
  BottomDivider,
  BottomItem,
  BottomRow,
  Container,
  FullDivider,
  HeroImageLink,
  OuterContainer,
  StyledContentCoverImage,
  StyledSecondaryItem,
  StyledSeriesPrimary,
  Title,
} from "./styles";

type Props = {
  className?: string;
  edges: batchQueryAbWidgetQuery$data["widget1"]["edges"];
  recommendationId?: string;
  topicName?: string;
  topicReference?: entityLink$key | null;
  widgetName: ArticleRecirculationWidgetName;
  widgetVersion?: WidgetVersion;
  withBlockSection?: boolean;
};

export const AiPrimaryWidget: FunctionComponent<Props> = ({
  className,
  edges,
  recommendationId,
  topicName,
  topicReference,
  widgetName,
  widgetVersion,
  withBlockSection = false,
}) => {
  const nodes = edges
    .map(edge => edge.node)
    .filter(notEmpty)
    .filter(node => notEmpty(node.entityId));

  const listOfArticles = nodes
    .map(node => node.entityId ?? "")
    .filter(Boolean)
    .join(",");

  const currentPageType = useCurrentPageType() as
    | PageType.Homepage
    | PageType.Section
    | PageType.SubSection;

  const impressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        ...(withBlockSection ? { block_section: topicName ?? "" } : {}),
        list_of_articles: listOfArticles,
        page_type: currentPageType,
        recommendation_id: recommendationId,
        result: "valid",
        widget_name: widgetName,
        ...(widgetVersion && { widget_version: widgetVersion }),
      },
      subcategory: "widget",
    }),
    [
      currentPageType,
      listOfArticles,
      recommendationId,
      topicName,
      widgetName,
      widgetVersion,
      withBlockSection,
    ],
  );

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: impressionGA4Event,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  if (nodes.length === 0) return null;

  const [primary, secondary, third] = nodes;

  const handleClick = (entityId?: string, urlAlias?: string) => {
    sendGA4Tracking({
      action: "click",
      category: "recirculation",
      customized_parameters: {
        article_id: entityId,
        ...(withBlockSection ? { block_section: topicName ?? "" } : {}),
        destination_url: getAbsoluteUrl(urlAlias),
        list_of_articles: listOfArticles,
        page_type: currentPageType,
        recommendation_id: recommendationId ?? "",
        result: "valid",
        widget_name: widgetName,
        ...(widgetVersion && { widget_version: widgetVersion }),
      },
      subcategory: "widget",
    });
  };

  const moduleSuffix = widgetVersion === "cold_start" ? "_coldstart" : "";

  return (
    <BaseLinkContextProvider customQueryParameters={{ module: `${widgetName}${moduleSuffix}` }}>
      <OuterContainer className={className} ref={captureTrackImpressionEventTargetElement}>
        <Title>
          {topicReference ? (
            <EntityLink reference={topicReference}>{topicName ?? "SCMP Series"}</EntityLink>
          ) : (
            (topicName ?? "SCMP Series")
          )}
        </Title>
        <Container>
          <HeroImageLink
            onClick={() => handleClick(primary.entityId, primary.urlAlias)}
            reference={primary}
          >
            <StyledContentCoverImage
              reference={primary}
              responsiveVariants={{
                desktopUp: "landscape250x99",
                mobileUp: "size540x360",
                tabletUp: "landscape250x99",
              }}
            />
          </HeroImageLink>
          <StyledSeriesPrimary
            isReadingTimeEnabled
            onClick={(entityId, urlAlias) => handleClick(entityId, urlAlias)}
            reference={primary}
            withComment
            withSummary
          />
          {(secondary || third) && (
            <>
              <FullDivider />
              <BottomRow>
                {secondary && (
                  <BottomItem>
                    <StyledSecondaryItem
                      isReadingTimeEnabled
                      onClick={(entityId, urlAlias) => handleClick(entityId, urlAlias)}
                      reference={secondary}
                      withComment
                    />
                  </BottomItem>
                )}
                {secondary && third && <BottomDivider />}
                {third && (
                  <BottomItem>
                    <StyledSecondaryItem
                      isReadingTimeEnabled
                      onClick={(entityId, urlAlias) => handleClick(entityId, urlAlias)}
                      reference={third}
                      withComment
                    />
                  </BottomItem>
                )}
              </BottomRow>
            </>
          )}
        </Container>
      </OuterContainer>
    </BaseLinkContextProvider>
  );
};

AiPrimaryWidget.displayName = "AiPrimaryWidget";
